import {
  completeQueryData,
  hasCompleteQueryData,
  throwOnQueryError,
} from 'ZalamoLibrary/src/lib/core/graphql/complete-query-data';

import { Observable, catchError, filter, first, map, of, pipe } from 'rxjs';

import { Injectable } from '@angular/core';

import {
  DeleteTabloMemberPhotoPositionGQL,
  DeleteTabloMemberPhotoPositionMutation,
  GetTabloMemberPhotoPositionGQL,
  GetTabloMemberPhotoPositionQuery,
  GetTabloMemberPhotoPositionsGQL,
  GetTabloMemberPhotoPositionsQuery,
  SaveTabloMemberPhotoPositionGQL,
  SaveTabloMemberPhotoPositionMutation,
  TabloMemberPhotoPositionInput,
} from 'projects/photographer-panel/src/app/modules/designer/graphql';
import { TabloFaceToPerson } from '@ui/tablo-generator/types';

type SavedTabloMemberPhotoPosition = NonNullable<
  SaveTabloMemberPhotoPositionMutation['saveTabloMemberPhotoPosition']
>;
type DeletedTabloMemberPhotoPosition = NonNullable<
  DeleteTabloMemberPhotoPositionMutation['deleteTabloMemberPhotoPosition']
>;
type LoadedTabloMemberPhotoPosition = NonNullable<
  GetTabloMemberPhotoPositionQuery['getTabloMemberPhotoPosition']
>;
type LoadedTabloMemberPhotoPositionsEntry = NonNullable<
  NonNullable<
    GetTabloMemberPhotoPositionsQuery['getTabloMemberPhotoPositions']
  >[number]
>;

@Injectable({
  providedIn: 'root',
})
export class TabloMemberPhotoPositionService {
  constructor(
    private readonly saveTabloMemberPhotoPositionGQL: SaveTabloMemberPhotoPositionGQL,
    private readonly getTabloMemberPhotoPositionGQL: GetTabloMemberPhotoPositionGQL,
    private readonly getTabloMemberPhotoPositionsGQL: GetTabloMemberPhotoPositionsGQL,
    private readonly deleteTabloMemberPhotoPositionGQL: DeleteTabloMemberPhotoPositionGQL,
  ) {}

  saveTabloMemberPhotoPosition(
    memberId: string,
    photoPosition: TabloFaceToPerson,
    contextId: string,
  ): Observable<SavedTabloMemberPhotoPosition | null> {
    const input: TabloMemberPhotoPositionInput = {
      memberId,
      photoId: photoPosition.photoId,
      groupId: photoPosition.groupId,
      contextId,
      x: photoPosition.x,
      y: photoPosition.y,
      width: photoPosition.width,
      height: photoPosition.height,
      scale: photoPosition.scale,
      rotation: photoPosition.rotation,
    };

    return this.saveTabloMemberPhotoPositionGQL
      .mutate({ variables: { input } })
      .pipe(
        map((result) => result.data?.saveTabloMemberPhotoPosition ?? null),
        catchError((error) => {
          console.error('Error saving tablo member photo position', error);
          return of(null);
        }),
      );
  }

  getTabloMemberPhotoPosition(
    memberId: string,
    contextId: string,
  ): Observable<TabloFaceToPerson | null> {
    return this.getTabloMemberPhotoPositionGQL
      .watch({
        ...{
          fetchPolicy: 'cache-and-network',
        },
        variables: {
          memberId,
          contextId,
        },
      })
      .valueChanges.pipe(
        throwOnQueryError,
        filter(hasCompleteQueryData),
        first(),
        map((result) =>
          this.mapTabloFaceToPerson(
            completeQueryData(result)?.getTabloMemberPhotoPosition ?? null,
          ),
        ),
        catchError((error) => {
          console.error('Error getting tablo member photo position', error);
          return of(null);
        }),
      );
  }

  getTabloMemberPhotoPositions(
    memberIds: string[],
    contextId: string,
  ): Observable<Record<string, TabloFaceToPerson>> {
    if (!memberIds?.length) {
      return of({});
    }

    return this.getTabloMemberPhotoPositionsGQL
      .watch({
        ...{
          fetchPolicy: 'cache-and-network',
        },
        variables: {
          memberIds,
          contextId,
        },
      })
      .valueChanges.pipe(
        throwOnQueryError,
        filter(hasCompleteQueryData),
        first(),
        map((result) => {
          const positions =
            completeQueryData(result)?.getTabloMemberPhotoPositions?.filter(
              (position): position is LoadedTabloMemberPhotoPositionsEntry =>
                !!position,
            ) ?? [];

          return positions.reduce<Record<string, TabloFaceToPerson>>(
            (accumulator, position) => {
              const mappedPosition = this.mapTabloFaceToPerson(position);
              if (mappedPosition && position.memberId) {
                accumulator[position.memberId] = mappedPosition;
              }
              return accumulator;
            },
            {},
          );
        }),
        catchError((error) => {
          console.error('Error getting tablo member photo positions', error);
          return of({});
        }),
      );
  }

  deleteTabloMemberPhotoPosition(
    memberId: string,
    contextId: string,
  ): Observable<DeletedTabloMemberPhotoPosition | null> {
    return this.deleteTabloMemberPhotoPositionGQL
      .mutate({
        variables: {
          memberId,
          contextId,
        },
      })
      .pipe(
        map((result) => result.data?.deleteTabloMemberPhotoPosition ?? null),
        catchError((error) => {
          console.error('Error deleting tablo member photo position', error);
          return of(null);
        }),
      );
  }

  private mapTabloFaceToPerson(
    position:
      | LoadedTabloMemberPhotoPosition
      | LoadedTabloMemberPhotoPositionsEntry
      | null,
  ): TabloFaceToPerson | null {
    if (!position) {
      return null;
    }

    return {
      photoId: position.photoId,
      groupId: position.groupId,
      x: position.x,
      y: position.y,
      width: position.width,
      height: position.height,
      scale: position.scale,
      rotation: position.rotation,
    };
  }
}
